import { TooltipWebV3 } from '@components/TaskList/TooltipWebV3';
import { DraggableAttributes } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity } from 'react-native';

import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import {
  getFirstLine,
  isMultiLine,
} from '@components/TaskList/TaskListItem.web';
import { EnhancedAnimatedCheckbox } from '@components/Web/AnimatedCheckbox';
import { TaskChecklist } from '@graphql/generated';
import { useOutsideClickAndEscape } from '@hooks/useOutsideClick.web';
import theme from '@themes/theme';

type TaskChecklistItemProps = {
  item?: TaskChecklist;
  width?: number;
  onCompletedTaskChecklistItem: (
    item: TaskChecklist,
    callback?: () => void
  ) => void;
  onSaveTaskChecklistItem: (name: string, item?: TaskChecklist) => void;
  onDeleteTaskChecklistItem: (itemId: string) => void;
  attributes?: DraggableAttributes;
  listeners?: SyntheticListenerMap | undefined;
  selectedItemId?: string | undefined;
  setSelectedItemId?: React.Dispatch<React.SetStateAction<string | undefined>>;
  canEditTaskSecondLevel: boolean;
};

export const TaskChecklistItem: React.FC<TaskChecklistItemProps> = ({
  item,
  width = 500,
  onCompletedTaskChecklistItem,
  onSaveTaskChecklistItem,
  onDeleteTaskChecklistItem,
  attributes,
  listeners,
  selectedItemId,
  setSelectedItemId,
  canEditTaskSecondLevel,
}) => {
  const MAX_TASK_NAME_LENGTH = 255;
  const MIN_INPUT_HEIGHT = 16;
  const MAX_INPUT_HEIGHT = 64;

  const inputRef = useRef<HTMLDivElement | null>(null);

  const [itemName, setItemName] = useState(item?.name ?? '');
  const [hovered, setHovered] = useState(false);
  const [hoveredAddItem, setHoveredAddItem] = useState(false);
  const [showInput, setShowInput] = useState(false);

  useEffect(() => {
    item?.name && setItemName(item?.name);
  }, [item]);

  const handleInput = useCallback((e: React.FormEvent<HTMLDivElement>) => {
    let text = e.currentTarget.innerText || '';

    if (text.length > MAX_TASK_NAME_LENGTH) {
      text = text.slice(0, MAX_TASK_NAME_LENGTH);
      e.currentTarget.innerText = text;
    }
    setItemName(text);
  }, []);

  const containerRef = useOutsideClickAndEscape<HTMLDivElement>(
    showInput,
    () => {
      if (itemName !== (item?.name ?? '')) {
        onSaveTaskChecklistItem(itemName, item);
        setItemName('');
      }
    },
    () => {
      setItemName(item?.name ?? '');
      setShowInput(false);
    }
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();

        if (itemName !== (item?.name ?? '')) {
          onSaveTaskChecklistItem(itemName, item);
          setItemName('');
        }

        inputRef.current?.blur(); // blur triggers collapse
        return;
      }

      const isTextKey =
        e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey;

      if (isTextKey && itemName.length >= MAX_TASK_NAME_LENGTH) {
        e.preventDefault();
      }
    },
    [itemName.length]
  );
  const textTaskNameRef = useRef<HTMLDivElement>(null);
  const [isTaskNameTruncated, setIsTaskNameTruncated] = useState(false);

  useEffect(() => {
    if (isMultiLine(item?.name ?? '')) {
      setIsTaskNameTruncated(true);
    } else {
      const el = textTaskNameRef.current;
      if (el) setIsTaskNameTruncated(el.scrollWidth > el.clientWidth);
    }
  }, [item]);
  // ⭐ NEW — Set the initial value into contentEditable when editing starts
  useEffect(() => {
    if (!showInput || !inputRef.current) return;

    const el = inputRef.current;
    el.innerText = itemName;

    // Move caret to end
    const timer = setTimeout(() => {
      const sel = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
      el.focus();
    }, 10);

    return () => clearTimeout(timer);
  }, [showInput]);

  // ⭐ NEW — When input loses focus, close editor
  const handleBlur = () => {
    setShowInput(false);
  };

  const { t } = useTranslation();

  const nameIsMultiLine = isMultiLine(item?.name ?? '');

  if (!showInput)
    return (
      <TouchableOpacity
        disabled={!canEditTaskSecondLevel}
        accessibilityLabel={item ? `Checklist Item | ${item.name}` : 'Add item'}
        onLongPress={() => {
          item && !item.completedAt && setSelectedItemId?.(item.id);
        }}
        onPress={() => {
          setHovered(false);
          setShowInput(true);
        }}>
        {item ? (
          <>
            {selectedItemId === item.id && !item.completedAt && (
              <>
                <Box
                  height={52}
                  width={width}
                  borderWidth={1}
                  borderColor='grey02'
                  borderRadius='xs'
                  backgroundColor='grey02'
                  style={{ position: 'absolute', top: 6 }}
                />
                <Box
                  flexDirection='row'
                  height={10}
                  style={{ position: 'absolute', top: 58 }}>
                  <Box
                    width={10}
                    height={10}
                    borderRadius='xs'
                    backgroundColor='greenSecondary'
                    style={{ position: 'absolute', left: 0, top: -4 }}
                  />
                  <Box
                    height={2}
                    width={width}
                    backgroundColor='greenSecondary'
                  />
                </Box>
              </>
            )}
            <Box
              accessibilityLabel='Checklist Item'
              height={52}
              width={width}
              mb='m'
              flexDirection='row'
              alignItems='center'
              borderWidth={1}
              borderColor='grey02'
              borderRadius='xs'
              pr='s'
              style={{
                backgroundColor: hovered
                  ? theme.colors.grey01
                  : theme.colors.white,
              }}
              {...attributes}
              {...listeners}
              onMouseEnter={() => canEditTaskSecondLevel && setHovered(true)}
              onMouseLeave={() => canEditTaskSecondLevel && setHovered(false)}>
              <Box
                width={20}
                alignItems='center'
                justifyContent='center'
                style={{ cursor: 'grab' }}>
                {hovered && !item.completedAt && (
                  <Icon name='Move1' variant='m' />
                )}
              </Box>

              {canEditTaskSecondLevel ? (
                <EnhancedAnimatedCheckbox
                  isHighlightColorGreen={true}
                  isHovered={hovered}
                  isChecked={item.completedAt !== null}
                  // This line of code affects drag-and-drop sorting.
                  // onHoverChange={setHovered}
                  onValueChange={() => {
                    if (item) {
                      onCompletedTaskChecklistItem(item, () => {
                        setHovered(false);
                      });
                    }
                  }}
                />
              ) : (
                <Icon
                  name={
                    item.completedAt !== null ? 'CheckBoxSelected' : 'Rectangle'
                  }
                />
              )}

              {canEditTaskSecondLevel || nameIsMultiLine ? (
                <TooltipWebV3
                  marginOffset={0}
                  disableHoverListener={!isTaskNameTruncated}
                  disableFocusListener={!isTaskNameTruncated}
                  disableTouchListener={
                    !isTaskNameTruncated || !canEditTaskSecondLevel
                  }
                  title={
                    <Text
                      numberOfLines={0}
                      maxWidth={300}
                      flexShrink={1}
                      variant='labelSmall'
                      color='white'>
                      {item.name}
                    </Text>
                  }
                  placement='bottom-start'>
                  <Box flex={1} alignItems='center'>
                    <Text
                      ref={textTaskNameRef}
                      ml='s'
                      mr='s'
                      flex={1}
                      flexShrink={1}
                      width={384}
                      numberOfLines={1}
                      ellipsizeMode='tail'
                      variant='webBodySecondary'
                      textDecorationLine={
                        item.completedAt === null ? 'none' : 'line-through'
                      }
                      color='textPrimary'
                      style={{ cursor: 'pointer' }}>
                      {nameIsMultiLine
                        ? getFirstLine(item.name) + ' ...'
                        : item.name}
                    </Text>
                  </Box>
                </TooltipWebV3>
              ) : (
                <Text
                  ref={textTaskNameRef}
                  ml='s'
                  mr='s'
                  flex={1}
                  flexShrink={1}
                  width={384}
                  numberOfLines={1}
                  ellipsizeMode='tail'
                  variant='webBodySecondary'
                  textDecorationLine={
                    item.completedAt === null ? 'none' : 'line-through'
                  }
                  color='textPrimary'>
                  {item.name}
                </Text>
              )}

              <Box
                width={24}
                height={24}
                ml='s'
                mr='m'
                alignItems='center'
                justifyContent='center'>
                {hovered && canEditTaskSecondLevel && (
                  <Icon
                    name='Close'
                    variant='s'
                    color='grey05'
                    onPress={() => onDeleteTaskChecklistItem(item?.id)}
                  />
                )}
              </Box>
            </Box>
          </>
        ) : (
          <Box
            mb='m'
            borderRadius='xs'
            backgroundColor={hoveredAddItem ? 'grey02' : 'grey01'}
            width={width}
            flexDirection='row'
            height={52}
            alignItems='center'
            onMouseEnter={() =>
              canEditTaskSecondLevel && setHoveredAddItem(true)
            }
            onMouseLeave={() =>
              canEditTaskSecondLevel && setHoveredAddItem(false)
            }>
            <Box width={20} height={20}></Box>
            <Icon color='greenSecondary' variant='s' name='Plus'></Icon>
            <Text ml='s' mr='m' variant='labelSmall' color='greenSecondary'>
              Add item
            </Text>
          </Box>
        )}
      </TouchableOpacity>
    );

  // EDIT MODE
  return (
    <div ref={containerRef}>
      <Box
        p='m'
        minHeight={98}
        width={width}
        mb='m'
        borderWidth={1}
        borderColor='grey02'
        borderRadius='xs'
        flexDirection='row'>
        <Box>
          <Box flexDirection='row' alignItems='center'>
            <Box
              flexDirection='row'
              width={432}
              borderRadius='xs'
              borderWidth={1}
              borderColor='black'
              px='m'
              py='s'>
              <div
                ref={inputRef}
                contentEditable
                suppressContentEditableWarning
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur} // ⭐ CLOSE ON BLUR
                style={{
                  width: 400,
                  outline: 'none',
                  cursor: 'text',
                  minHeight: MIN_INPUT_HEIGHT,
                  maxHeight: MAX_INPUT_HEIGHT,
                  overflowY: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  display: 'flex',
                  alignItems: isMultiLine(itemName) ? 'flex-start' : 'center',
                  fontSize: theme.textVariants.webBodySecondary.fontSize,
                  fontFamily: theme.textVariants.webBodySecondary.fontFamily,
                  lineHeight:
                    theme.textVariants.webBodySecondary.lineHeight + 'px',
                  color: theme.colors.textSecondary,
                }}
                data-placeholder='Item name...'
              />
              <Text
                ml='s'
                variant='bodySecondary'
                color='grey04'
                alignSelf='flex-end'>
                {255 - itemName.length}
              </Text>
            </Box>

            <Box
              width={16}
              height={16}
              ml='s'
              alignItems='center'
              justifyContent='center'>
              <Icon
                name='Close'
                variant='s'
                color='grey05'
                onPress={() => {
                  if (item) {
                    onDeleteTaskChecklistItem(item.id);
                  } else {
                    setItemName('');
                  }
                  setShowInput(false);
                }}
              />
            </Box>
          </Box>

          <Text
            ml='m'
            variant='webBodyHintSubline'
            color='grey04'
            style={{ marginTop: 10 }}>
            {t('models:projects.checklist.pressShiftEnterForNewLine')}
          </Text>
        </Box>
      </Box>
    </div>
  );
};
